
import { FAQResponse } from '@/components/faq-serp/types';
import { logger } from '@/utils/logger';
import { isHtmlResponse, extractErrorFromHtml } from '@/hooks/api/apiValidation';

/**
 * Parse JSON response with enhanced error handling
 */
export function parseJsonResponse(responseText: string | object): FAQResponse {
  console.log('🔍 FAQ Expert: Starting response parsing...');
  
  try {
    // First check if we received an object directly (which happens with json_object format)
    if (typeof responseText === 'object' && responseText !== null) {
      console.log('📦 FAQ Expert: Received object response');
      
      // If we have a direct object, verify it has the expected structure
      if ('faq' in responseText) {
        const faqResponse = responseText as FAQResponse;
        
        // Validate response quality
        const qualityCheck = validateFaqQuality(faqResponse);
        if (!qualityCheck.isValid) {
          console.error('⚠️ FAQ Expert: Response quality validation failed:', qualityCheck.issues);
          throw new Error(`Generated FAQs appear to be generic or low quality: ${qualityCheck.issues.join(', ')}`);
        }
        
        console.log('✅ FAQ Expert: Response validation passed');
        return faqResponse;
      } else {
        console.error('❌ FAQ Expert: Response object does not have faq property:', responseText);
        throw new Error('API response does not have the expected FAQ format');
      }
    }
    
    // Handle string responses
    if (typeof responseText === 'string') {
      // Check if we received HTML instead of JSON
      if (isHtmlResponse(responseText)) {
        console.error('Received HTML instead of JSON:', responseText.substring(0, 500));
        const { message, code } = extractErrorFromHtml(responseText);
        throw new Error(`API returned HTML instead of JSON (${code}): ${message}`);
      }
      
      // Ensure the response is valid JSON before parsing
      if (responseText.trim().startsWith('{') && responseText.trim().endsWith('}')) {
        // Parse the JSON response
        const parsed = JSON.parse(responseText) as any;
        
        // Verify it has the expected structure
        if (!parsed.faq) {
          throw new Error('API response does not have the expected FAQ format');
        }
        
        return parsed as FAQResponse;
      } else {
        // Response is not JSON format
        console.error('Error: Response is not in JSON format.');
        console.error('Raw response first 500 chars:', responseText.substring(0, 500));
        throw new Error('API did not return valid JSON. Response does not start with "{" and end with "}"');
      }
    }
    
    // If we get here, something unexpected happened
    throw new Error(`Unexpected response type: ${typeof responseText}`);
  } catch (parseError) {
    console.error('Error parsing JSON response:', parseError);
    if (typeof responseText === 'string') {
      console.error('Raw response first 500 chars:', responseText.substring(0, 500));
    } else {
      console.error('Raw response (object):', responseText);
    }
    
    // Enhanced error logging for JSON parsing issues
    logger.error('JSON parsing error in FAQ generation', parseError as Error);
    
    throw new Error(`Failed to parse API response as JSON: ${(parseError as Error).message}`);
  }
}

/**
 * Validate FAQ response quality to detect generic content
 */
function validateFaqQuality(faqResponse: FAQResponse): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];
  const faq = faqResponse.faq;
  
  // Collect all questions and answers for analysis
  const allQuestions: string[] = [];
  const allAnswers: string[] = [];
  
  Object.values(faq).forEach(categoryFaqs => {
    categoryFaqs.forEach(item => {
      allQuestions.push(item.question.toLowerCase());
      if (typeof item.answer === 'string') {
        allAnswers.push(item.answer.toLowerCase());
      } else if (item.answer && typeof item.answer === 'object') {
        const answerText = [
          item.answer.intro || '',
          ...(item.answer.details || []),
          item.answer.conclusion || ''
        ].join(' ').toLowerCase();
        allAnswers.push(answerText);
      }
    });
  });
  
  // Check for generic security/business indicators
  const genericSecurityTerms = [
    'non-human identity', 'ephemeral identities', 'credential management',
    'attack surface', 'security posture', 'identity management',
    'authentication', 'authorization', 'cybersecurity'
  ];
  
  const genericBusinessTerms = [
    'content marketing', 'brand awareness', 'engagement metrics',
    'conversion metrics', 'seo optimization', 'social media platforms'
  ];
  
  const allContent = [...allQuestions, ...allAnswers].join(' ');
  
  // Check for excessive generic security terms
  const securityMatches = genericSecurityTerms.filter(term => 
    allContent.includes(term.toLowerCase())
  );
  
  if (securityMatches.length > 3) {
    issues.push(`Contains too many generic security terms: ${securityMatches.join(', ')}`);
  }
  
  // Check for excessive generic business terms
  const businessMatches = genericBusinessTerms.filter(term => 
    allContent.includes(term.toLowerCase())
  );
  
  if (businessMatches.length > 3) {
    issues.push(`Contains too many generic business terms: ${businessMatches.join(', ')}`);
  }
  
  // Check for repetitive questions
  const questionWords = allQuestions.join(' ').split(' ');
  const wordFreq = questionWords.reduce((acc, word) => {
    if (word.length > 4) {
      acc[word] = (acc[word] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
  
  const overusedWords = Object.entries(wordFreq)
    .filter(([word, count]) => count > 2)
    .map(([word]) => word);
  
  if (overusedWords.length > 2) {
    issues.push(`Questions appear repetitive with overused terms: ${overusedWords.join(', ')}`);
  }
  
  console.log(`🔍 FAQ Expert: Quality validation - ${issues.length} issues found`);
  if (issues.length > 0) {
    console.log('⚠️ FAQ Expert: Quality issues:', issues);
  }
  
  return {
    isValid: issues.length === 0,
    issues
  };
}
